import { ScheduledAction, TaskItemForSchedule } from '@demind-inc/core';
import dayjs from 'dayjs';

export const WEEKLY_PLAN_JOB_TYPE = 'weekly_plan' as const;

/** Just enough of a task to map a planned event back to its todo. Persisted
 * with the job id so a resumed run can rebuild `todoFrom` without the
 * original task list. */
export type WeeklyPlanTaskRef = Pick<TaskItemForSchedule, 'taskId' | 'boardId' | 'appFrom'>;

/** Persisted alongside the weekly-plan job_id so a resumed poll can replay
 * events the way the live run would have (which dates are in flight, and
 * which todos the events refer to). */
export interface WeeklyPlanJobContext {
  weekStart: string;
  numDays: number;
  tasks: WeeklyPlanTaskRef[];
}

export interface WeeklyPlanStreamResult {
  schedules: ScheduledAction[];
  summary: string | null;
}

export const toWeeklyPlanTaskRefs = (tasks?: TaskItemForSchedule[]): WeeklyPlanTaskRef[] =>
  (tasks ?? []).map(({ taskId, boardId, appFrom }) => ({ taskId, boardId, appFrom }));

export const getWeekPlanDates = (weekStart: string, numDays: number): string[] =>
  Array.from({ length: Math.max(1, numDays) }, (_, index) =>
    dayjs(weekStart).add(index, 'day').format('YYYY-MM-DD')
  );

const toISOFromDateAndTime = (date: string, hhmm: string) => {
  const [hour, minute] = hhmm.split(':').map((v) => Number(v || 0));
  return dayjs(date).hour(hour).minute(minute).second(0).millisecond(0).toISOString();
};

const parseScheduleDayOffset = (value: unknown): number => {
  if (value === null || value === undefined) {
    return 0;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

export const transformWeekPlanDayEventsToScheduledActions = (
  date: string,
  events: Array<Record<string, any>>,
  originalTasks?: WeeklyPlanTaskRef[]
): ScheduledAction[] => {
  return events.map((event, index) => {
    const baseDay = dayjs(date, 'YYYY-MM-DD').startOf('day');
    const startOffset = parseScheduleDayOffset(event.start_day_offset ?? event.startDayOffset);
    const endOffset = parseScheduleDayOffset(event.end_day_offset ?? event.endDayOffset);

    const startDayJs = event.start_date
      ? dayjs(String(event.start_date), 'YYYY-MM-DD').startOf('day')
      : baseDay.add(startOffset, 'day');
    const endDayJs = event.end_date
      ? dayjs(String(event.end_date), 'YYYY-MM-DD').startOf('day')
      : baseDay.add(endOffset, 'day');

    const startDate = toISOFromDateAndTime(
      startDayJs.format('YYYY-MM-DD'),
      String(event.start_time || '00:00')
    );
    let endDate = toISOFromDateAndTime(
      endDayJs.format('YYYY-MM-DD'),
      String(event.end_time || '00:00')
    );
    if (dayjs(endDate).isBefore(dayjs(startDate))) {
      endDate = dayjs(endDate).add(1, 'day').toISOString();
    }

    const originalTask = originalTasks?.find((task) => task.taskId === event.origin_todo_id);

    return {
      id: event.action_id || event.event_id || `${date}-${event.name || 'event'}-${index}`,
      startDate,
      endDate,
      name: event.name || 'Scheduled event',
      categoryId: event.category || 'other',
      description: event.explanation || event.description || '',
      eventFrom: event.event_from || 'existing_calendar',
      todoFrom:
        event.origin_todo_id && originalTask
          ? {
              taskId: originalTask.taskId,
              boardId: originalTask.boardId!,
              from: originalTask.appFrom!,
            }
          : undefined,
      routineFrom: event.origin_routine_activity_id
        ? { routineActivityId: event.origin_routine_activity_id }
        : undefined,
      actionType: event.schedule_action || 'add',
    } as ScheduledAction;
  });
};

export const sortScheduledActionsChronologically = (
  schedules: ScheduledAction[]
): ScheduledAction[] =>
  [...schedules].sort((a, b) => {
    const aTime = a.startDate ? dayjs(a.startDate).valueOf() : 0;
    const bTime = b.startDate ? dayjs(b.startDate).valueOf() : 0;
    return aTime - bTime;
  });

/** Fold one `day_complete` payload into the running result. Days complete in
 * any order, so schedules are re-sorted every time. Returns a new object. */
export const applyWeekPlanDayComplete = (
  result: WeeklyPlanStreamResult,
  data: { date?: string; events?: unknown; summary?: string | null },
  weekStart: string,
  tasks?: WeeklyPlanTaskRef[]
): WeeklyPlanStreamResult => {
  const daySchedules = transformWeekPlanDayEventsToScheduledActions(
    data.date || weekStart,
    Array.isArray(data.events) ? data.events : [],
    tasks
  );
  return {
    schedules: sortScheduledActionsChronologically([...result.schedules, ...daySchedules]),
    summary: data.summary ?? result.summary ?? null,
  };
};

/** Status line carried by a persisted `status_text` / `plan_action` event, in
 * the same wording the live stream shows. Null for any other event. */
export const extractWeekPlanStatusText = (type: string, data: unknown): string | null => {
  if (typeof data === 'string') {
    return type === 'status_text' && data ? data : null;
  }
  if (!data || typeof data !== 'object') {
    return null;
  }
  const payload = data as Record<string, unknown>;
  if (type === 'status_text') {
    const text = payload.status_text ?? payload.statusText ?? payload.status;
    return typeof text === 'string' && text ? text : null;
  }
  if (type === 'plan_action') {
    const action = typeof payload.action === 'string' ? payload.action : 'update';
    const item = typeof payload.item === 'string' && payload.item ? `: ${payload.item}` : '';
    const time = typeof payload.time === 'string' && payload.time ? ` (${payload.time})` : '';
    const reason = typeof payload.reason === 'string' && payload.reason ? ` — ${payload.reason}` : '';
    return `${action}${item}${time}${reason}`.trim();
  }
  return null;
};
